import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LoadingButton from '@mui/lab/LoadingButton';
import ImeiDialog from './ImeiDialog';
import PageHeader from '../common/PageHeader';
import MasterDrawer from '../Master/create/MasterDrawer';
import ProductForm from '../Product/ProductForm';
import ProductProperties from '../Product/ProductProperties';
import { useGetSuppliersQuery } from '../../api/master/MasterApi';
import { useLazySearchProductsQuery } from '../../api/product/ProductApi';
import { useSavePurchaseMutation } from '../../api/purchase/PurchaseApi';
import { Product } from '../../types/product';
import { ImeiEntry, PurchaseRow } from '../../types/purchase';
import { useSnackbar } from '../../utils/snackbar';
import { shortDate } from '../../utils/dates';

/** The item table's head, in the app's own blue - the same table billing uses. */
const headCell = {
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  px: 1.5,
  py: 1,
};

const today = () => new Date().toISOString().slice(0, 10);
const newKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Purchase entry.
 *
 * <p>One product box - type a name, a barcode, a brand or a model and pick what
 * comes up. Whether the line then asks for serial numbers is the product's own
 * business, not a choice the person entering it has to make.
 *
 * <p>Each line becomes a batch with its own cost, prices and warranty, so buying the
 * same handset again next month at a different price keeps the two apart.
 */
const PurchaseAdd = ({ onDone }: { onDone: () => void }) => {
  const { openSnackbar } = useSnackbar();
  const [savePurchase, { isLoading: saving }] = useSavePurchaseMutation();

  /* invoice header */
  const [supplierId, setSupplierId] = useState<number | null>(null);
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [remarks, setRemarks] = useState('');
  const [addingSupplier, setAddingSupplier] = useState(false);

  /* the line being built */
  const [product, setProduct] = useState<Product | null>(null);
  const [productInput, setProductInput] = useState('');
  const [options, setOptions] = useState<Product[]>([]);
  const [quantity, setQuantity] = useState('1');
  const [cost, setCost] = useState('');
  const [mrp, setMrp] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [batchNo, setBatchNo] = useState('');
  const [gstPercentage, setGstPercentage] = useState('18');
  const [warrantyValue, setWarrantyValue] = useState('12');
  const [warrantyUnit, setWarrantyUnit] = useState<'MONTHS' | 'YEARS' | 'DAYS' | 'DATE'>('MONTHS');
  const [warrantyDate, setWarrantyDate] = useState('');

  const [rows, setRows] = useState<PurchaseRow[]>([]);
  const [imeiRowKey, setImeiRowKey] = useState<string | null>(null);
  const [addingProduct, setAddingProduct] = useState(false);
  /** Lines whose GST differs from the product master - asked about before saving. */
  const [gstMismatches, setGstMismatches] = useState<PurchaseRow[]>([]);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data: suppliers = [], refetch: refetchSuppliers } = useGetSuppliersQuery();
  const [searchProducts] = useLazySearchProductsQuery();

  /** Reads the list straight away - used when the box is opened, not typed into. */
  const loadProducts = useCallback(
    async (tag = '') => {
      try {
        setOptions(await searchProducts(tag).unwrap());
      } catch {
        setOptions([]);
      }
    },
    [searchProducts],
  );

  // The box shows what the shop sells the moment it is opened. Before this it sat
  // empty until something was typed, which read as "there are no products".
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // A pending lookup must not fire after the screen has gone.
  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    [],
  );

  /** Looks products up 400 ms after typing stops, not on every key. */
  const onProductTyped = (value: string) => {
    setProductInput(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => loadProducts(value.trim()), 400);
  };

  const pickProduct = (picked: Product | null) => {
    setProduct(picked);
    if (!picked) return;
    // Start from what the master knows; the buyer overwrites whatever changed.
    setMrp(picked.mrp ? String(picked.mrp) : '');
    setSellingPrice(picked.sellingPrice ? String(picked.sellingPrice) : '');
    setGstPercentage(picked.gstPercentage ? String(picked.gstPercentage) : '18');
  };

  /**
   * The cost typed against a line is what the goods came to BEFORE tax, so GST is added
   * on top. Same rule as billing, and the same arithmetic the server does on save - this
   * is only the preview, and the two must never disagree.
   *
   * <p>A line left at 0 GST simply has nothing added: that is a purchase without GST.
   */
  const totals = useMemo(() => {
    const taxable = rows.reduce((sum, row) => sum + lineTotal(row), 0);
    const tax = rows.reduce((sum, row) => {
      const gst = Number(row.gstPercentage) || 0;
      if (!gst) return sum;
      return sum + (lineTotal(row) * gst) / 100;
    }, 0);
    return { total: taxable + tax, tax, taxable };
  }, [rows]);

  /** Products already listed under the same name - the ones easy to mix up. */
  const sameNameCount = useMemo(() => {
    if (!product?.productName) return 0;
    const name = product.productName.trim().toLowerCase();
    return options.filter((option) => (option.productName ?? '').trim().toLowerCase() === name).length;
  }, [options, product]);

  const addRow = () => {
    if (!product?.id) {
      openSnackbar('Pick a product first', 'warning');
      return;
    }
    const qty = Number(quantity);
    if (!qty || qty <= 0) {
      openSnackbar('Quantity must be more than zero', 'warning');
      return;
    }
    // Checked here as well as shown on the box. The red field is only a hint - Add
    // is an ordinary button, so nothing stopped a blank cost reaching the line as
    // zero, and a batch that cost nothing makes every profit figure from it wrong.
    if (!Number(cost) || Number(cost) <= 0) {
      openSnackbar('Enter what one piece cost', 'warning');
      return;
    }

    const key = newKey();
    const serialTracked = product.serialTracked !== false;

    setRows((previous) => [
      ...previous,
      {
        key,
        productId: product.id as number,
        productName: product.productName,
        serialTracked,
        label: product.productName ?? '',
        quantity: qty,
        // Blank means the backend builds one from the invoice and line number.
        batchNo: batchNo.trim() || null,
        purchasePrice: Number(cost) || 0,
        mrp: mrp === '' ? null : Number(mrp),
        sellingPrice: sellingPrice === '' ? null : Number(sellingPrice),
        gstPercentage: gstPercentage === '' ? null : Number(gstPercentage),
        // Kept beside it so Save can notice the two disagree.
        productGst: product.gstPercentage ?? null,
        ...warrantyFor(invoiceDate, warrantyUnit, warrantyValue, warrantyDate),
        commonPrice: true,
        imeis: [],
      },
    ]);

    setProduct(null);
    setProductInput('');
    setQuantity('1');
    setBatchNo('');
    setCost('');
    setMrp('');
    setSellingPrice('');
    // Straight back to the full list: an invoice is usually several products.
    loadProducts();

    // A handset line is not finished until its serial numbers are in, so the box
    // for them opens itself rather than waiting to be found on the row.
    if (serialTracked) {
      setImeiRowKey(key);
    }
  };

  const imeiRow = rows.find((row) => row.key === imeiRowKey) || null;
  const imeisUsedElsewhere = useMemo(
    () =>
      rows
        .filter((row) => row.key !== imeiRowKey)
        .flatMap((row) => row.imeis.map((entry) => entry.imeiNo))
        .filter(Boolean),
    [rows, imeiRowKey],
  );

  const saveImeis = (key: string, imeis: ImeiEntry[], commonPrice: boolean) => {
    setRows((previous) => previous.map((row) => (row.key === key ? { ...row, imeis, commonPrice } : row)));
    setImeiRowKey(null);
  };

  /**
   * Save, but ask first if any line's tax rate disagrees with the product.
   *
   * <p>A rate typed on a supplier invoice only fixes that invoice; customers are
   * charged what the product says. Left alone, the two drift apart silently - so the
   * shop is shown the difference and decides, rather than the software guessing.
   */
  const handleSave = async () => {
    const differing = rows.filter(
      (row) =>
        row.gstPercentage !== null &&
        row.gstPercentage !== undefined &&
        row.productGst !== null &&
        row.productGst !== undefined &&
        Number(row.gstPercentage) !== Number(row.productGst),
    );
    if (differing.length > 0) {
      setGstMismatches(differing);
      return;
    }
    await save(false);
  };

  const save = async (updateProductGst: boolean) => {
    setGstMismatches([]);
    if (!supplierId) {
      openSnackbar('Choose a supplier', 'warning');
      return;
    }
    if (!invoiceNo.trim()) {
      openSnackbar('Enter the invoice number', 'warning');
      return;
    }
    // The asterisk on the box is a native form rule, and this screen saves from a
    // button rather than by submitting a form - so without this a cleared date went
    // through as an empty string and the purchase landed with no date on it.
    if (!invoiceDate) {
      openSnackbar('Choose the invoice date', 'warning');
      return;
    }
    if (rows.length === 0) {
      openSnackbar('Add at least one item', 'warning');
      return;
    }

    const missing = rows.find((row) => row.serialTracked && row.imeis.length !== Number(row.quantity));
    if (missing) {
      openSnackbar(`Enter ${missing.quantity} serial number(s) for ${missing.label}`, 'warning');
      // Straight to the line that is short, rather than leaving them to find it.
      setImeiRowKey(missing.key);
      return;
    }

    /*
     * The row's own cost is checked as it is added, but a line where the shop unticked
     * "Same cost for every phone" is priced piece by piece inside the serial number
     * dialog instead - so the row-level check never sees those numbers. The dialog now
     * refuses to save an empty one; this is the same rule at the door, for a line that
     * was priced before that rule existed or edited afterwards.
     */
    const freeHandset = rows.find(
      (row) => row.serialTracked && row.commonPrice === false && row.imeis.some((entry) => !(Number(entry.purchasePrice) > 0)),
    );
    if (freeHandset) {
      openSnackbar(`Enter what each piece of ${freeHandset.label} cost`, 'warning');
      setImeiRowKey(freeHandset.key);
      return;
    }

    try {
      await savePurchase({
        invoiceNo: invoiceNo.trim(),
        invoiceDate,
        supplierId,
        remarks: remarks.trim(),
        updateProductGst,
        items: rows.map(({ key, label, serialTracked, productName, productGst, ...item }) => item),
      }).unwrap();
      openSnackbar('Purchase saved, stock and batches updated', 'success');
      onDone();
    } catch (error: any) {
      // The backend refuses duplicate serial numbers and repeated invoices, and says which.
      openSnackbar(error?.data?.message || 'Could not save the purchase', 'error');
    }
  };

  const sectionTitle = (label: string, hint?: string) => (
    <Stack direction="row" alignItems="baseline" gap={1} sx={{ mb: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
        {label}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Stack>
  );

  return (
    <Box>
      <PageHeader title="Purchase Entry" subtitle="Each line becomes a batch with its own cost and prices">
        <Button variant="outlined" color="inherit" size="small" startIcon={<ArrowBackIcon />} onClick={onDone}>
          Back
        </Button>
      </PageHeader>

      <Card sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
        {sectionTitle('Invoice Details')}
        {/* One row, one baseline: every box is the same height and the + sits on
            the supplier box rather than beside it, so nothing steps up or down. */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            {/* The + is its own button next to the box - every field in the row is
                the same height, so they line up without it being inside one. */}
            <Stack direction="row" gap={0.5} alignItems="center">
              <Autocomplete
                fullWidth
                size="small"
                options={suppliers}
                value={suppliers.find((supplier: any) => supplier.id === supplierId) ?? null}
                getOptionLabel={(option: any) => option.supplierName ?? ''}
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                onChange={(_event, value: any) => setSupplierId(value?.id ?? null)}
                renderInput={(params) => <TextField {...params} label="Supplier" required error={!supplierId} helperText={!supplierId ? 'Choose who the goods came from' : ' '} />}
              />
              <Tooltip title="New supplier">
                <IconButton
                  color="primary"
                  tabIndex={-1}
                  onClick={() => setAddingSupplier(true)}
                  aria-label="New supplier"
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, width: 40, height: 40 }}
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              label="Invoice No"
              size="small"
              fullWidth
              required
              error={!invoiceNo.trim()}
              helperText={!invoiceNo.trim() ? "The supplier's invoice number" : ' '}
              value={invoiceNo}
              onChange={(event) => setInvoiceNo(event.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              label="Invoice Date"
              required
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={invoiceDate}
              onChange={(event) => setInvoiceDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField label="Remarks" size="small" fullWidth value={remarks} onChange={(event) => setRemarks(event.target.value)} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {sectionTitle('Add Item', 'one line, one batch - leave the batch number blank and it is made from the invoice')}
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={4}>
            <Stack direction="row" gap={0.5} alignItems="center">
              <Autocomplete
                fullWidth
                size="small"
                options={options}
                value={product}
                filterOptions={(all) => all}
                inputValue={productInput}
                getOptionLabel={(option) => option.productName ?? ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input') onProductTyped(value);
                }}
                onChange={(_event, value) => {
                  pickProduct(value);
                  setProductInput(value?.productName ?? '');
                }}
                noOptionsText="No products found. Use + to create one."
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">{option.productName}</Typography>
                        {/* Colour and memory are on the option itself: two products can
                            carry the same name and be different things. */}
                        <Typography variant="caption" color="text.secondary">
                          {[option.brandName, option.modelName, option.colour, option.ram, option.storage, option.barcode]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={option.serialTracked === false ? 'No serial number' : 'Serial number'}
                      />
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Product" required placeholder="Type a name, barcode, brand or model..." autoFocus />
                )}
              />
              <Tooltip title="New product">
                <IconButton
                  color="primary"
                  tabIndex={-1}
                  onClick={() => setAddingProduct(true)}
                  aria-label="New product"
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, width: 40, height: 40 }}
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
          <Grid item xs={4} sm={2} md={1}>
            {/* Both are what the line is worth, so both are required and say so on the
                box rather than only in a message after Add is pressed. */}
            <TextField
              label="Qty"
              type="number"
              size="small"
              fullWidth
              required
              error={!!product && (!Number(quantity) || Number(quantity) <= 0)}
              helperText={!!product && (!Number(quantity) || Number(quantity) <= 0) ? 'More than zero' : ' '}
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
            />
          </Grid>
          <Grid item xs={8} sm={3} md={1.5}>
            <TextField
              label="Batch No"
              size="small"
              fullWidth
              placeholder="Auto"
              value={batchNo}
              onChange={(event) => setBatchNo(event.target.value)}
            />
          </Grid>
          <Grid item xs={8} sm={3} md={1.5}>
            <TextField
              label="Cost / piece (before GST)"
              type="number"
              size="small"
              fullWidth
              required
              error={!!product && (!Number(cost) || Number(cost) <= 0)}
              helperText={!!product && (!Number(cost) || Number(cost) <= 0) ? 'What one piece cost' : ' '}
              value={cost}
              onChange={(event) => setCost(event.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField label="MRP" type="number" size="small" fullWidth value={mrp} onChange={(event) => setMrp(event.target.value)} />
          </Grid>
          <Grid item xs={6} sm={4} md={1.5}>
            <TextField
              label="Selling Price"
              type="number"
              size="small"
              fullWidth
              value={sellingPrice}
              onChange={(event) => setSellingPrice(event.target.value)}
            />
          </Grid>
          <Grid item xs={4} sm={2} md={1}>
            <TextField label="GST %" type="number" size="small" fullWidth value={gstPercentage} onChange={(event) => setGstPercentage(event.target.value)} />
          </Grid>
          <Grid item xs={8} sm={4} md={2}>
            <Stack direction="row" gap={1}>
              {warrantyUnit === 'DATE' ? (
                <TextField
                  label="Warranty till"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={warrantyDate}
                  onChange={(event) => setWarrantyDate(event.target.value)}
                />
              ) : (
                <TextField
                  label="Warranty"
                  type="number"
                  size="small"
                  fullWidth
                  value={warrantyValue}
                  onChange={(event) => setWarrantyValue(event.target.value)}
                />
              )}
              <TextField
                select
                size="small"
                sx={{ width: 110 }}
                value={warrantyUnit}
                onChange={(event) => setWarrantyUnit(event.target.value as typeof warrantyUnit)}
              >
                <MenuItem value="MONTHS">Months</MenuItem>
                <MenuItem value="YEARS">Years</MenuItem>
                <MenuItem value="DAYS">Days</MenuItem>
                <MenuItem value="DATE">Till date</MenuItem>
              </TextField>
            </Stack>
          </Grid>
          <Grid item xs={4} sm={2} md={1.5}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={addRow} fullWidth>
              Add
            </Button>
          </Grid>
        </Grid>

        {product && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'action.hover' }}>
            <ProductProperties product={product} sameName={sameNameCount} />
            {product.serialTracked !== false && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
                This one is followed by serial number - the box for them opens as soon as you press Add.
              </Typography>
            )}
          </Paper>
        )}
      </Card>

      <Paper sx={{ overflow: 'auto' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {/* Each heading carries its own alignment so it sits over its own data.
                    They were all left while the cells underneath were a mixture of left
                    and centre, so no column lined up with its own heading - and the
                    money columns, being left, had their digits in a different place on
                    every row. Numbers go right, where units sit under units. */}
                {[
                  { head: 'Product', align: 'left' as const },
                  { head: 'Batch', align: 'left' as const },
                  { head: 'Qty', align: 'right' as const },
                  { head: 'Cost', align: 'right' as const },
                  { head: 'Sells for', align: 'right' as const },
                  { head: 'Warranty', align: 'center' as const },
                  { head: 'GST %', align: 'right' as const },
                  { head: 'Total', align: 'right' as const },
                  { head: 'Serial No', align: 'center' as const },
                  { head: 'Action', align: 'center' as const },
                ].map(({ head, align }) => (
                  <TableCell key={head} align={align} sx={headCell}>
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                    No items yet. Find a product above and press Add.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const complete = !row.serialTracked || row.imeis.length === Number(row.quantity);
                  return (
                    <TableRow key={row.key} hover sx={{ '& td': { py: 0.5, px: 1 } }}>
                      <TableCell sx={{ minWidth: 200, fontSize: 12, fontWeight: 500 }}>{row.label}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {row.batchNo ? (
                          <Chip size="small" variant="outlined" label={row.batchNo} />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            auto
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>{row.quantity}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>{row.commonPrice === false ? 'Per piece' : row.purchasePrice}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>{row.sellingPrice ?? '-'}</TableCell>
                      <TableCell sx={{ fontSize: 12, textAlign: 'center' }}>{warrantyLabel(row)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>{row.gstPercentage ?? '-'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{lineTotal(row).toFixed(2)}</TableCell>
                      <TableCell align="center">
                        {row.serialTracked ? (
                          <Chip
                            size="small"
                            icon={<QrCode2Icon />}
                            color={complete ? 'success' : 'warning'}
                            variant={complete ? 'filled' : 'outlined'}
                            label={
                              complete
                                ? `${row.imeis.length}/${row.quantity} entered`
                                : `Enter serial no (${row.imeis.length}/${row.quantity})`
                            }
                            onClick={() => setImeiRowKey(row.key)}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Not needed
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setRows((previous) => previous.filter((item) => item.key !== row.key))}
                            aria-label="Remove"
                          >
                            <RemoveCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Card sx={{ p: 2, minWidth: 280, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
            SUMMARY
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2">Lines</Typography>
            <Typography variant="body2">{rows.length}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2">Taxable</Typography>
            <Typography variant="body2">{totals.taxable.toFixed(2)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="body2">GST</Typography>
            <Typography variant="body2">{totals.tax.toFixed(2)}</Typography>
          </Stack>
          <Divider sx={{ mb: 1 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Invoice Total
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {totals.total.toFixed(2)}
            </Typography>
          </Stack>
        </Card>
      </Box>

      <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 2 }}>
        <Button variant="outlined" color="inherit" onClick={onDone}>
          Cancel
        </Button>
        <LoadingButton variant="contained" loading={saving} onClick={handleSave}>
          Save Purchase
        </LoadingButton>
      </Stack>

      {/* The tax rate on this invoice differs from the master. The shop decides which
          one is right - the software will not guess, because the product's rate is
          what every future customer is charged. */}
      <Dialog open={gstMismatches.length > 0} onClose={() => setGstMismatches([])} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">
            This invoice uses a different GST rate
          </Typography>
          <Typography variant="caption" color="text.secondary">
            The product's rate is what customers are charged on every future bill
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="center">Product says</TableCell>
                <TableCell align="center">This invoice says</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gstMismatches.map((row) => (
                <TableRow key={row.key}>
                  <TableCell sx={{ fontWeight: 500 }}>{row.label}</TableCell>
                  <TableCell align="center">{row.productGst}%</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: 'warning.main' }}>
                    {row.gstPercentage}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            If the rate has genuinely changed, update the product so customers are charged correctly. If you only typed it
            for this invoice, keep the product as it is.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setGstMismatches([])}>
            Back to the invoice
          </Button>
          <Box flexGrow={1} />
          <Button variant="outlined" size="small" onClick={() => save(false)}>
            Save, keep product as it is
          </Button>
          <LoadingButton variant="contained" size="small" loading={saving} onClick={() => save(true)}>
            Save and update the product
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {imeiRow && (
        <ImeiDialog
          row={imeiRow}
          usedElsewhere={imeisUsedElsewhere}
          onClose={() => setImeiRowKey(null)}
          onSave={(imeis, commonPrice) => saveImeis(imeiRow.key, imeis, commonPrice)}
        />
      )}

      {/* Created here, bought here: the new product lands on the line at once. */}
      <Dialog open={addingProduct} onClose={() => setAddingProduct(false)} maxWidth="lg" fullWidth>
        <ProductForm
          product={null}
          onClose={(created) => {
            setAddingProduct(false);
            if (created) {
              pickProduct(created);
              setProductInput(created.productName ?? '');
            }
          }}
        />
      </Dialog>

      {addingSupplier && (
        <MasterDrawer
          tab="SUPPLIER"
          editData={null}
          closeDrawer={() => {
            setAddingSupplier(false);
            refetchSuppliers();
          }}
        />
      )}
    </Box>
  );
};

/**
 * Warranty, however the shop chose to give it.
 *
 * <p>Months is what a phone warranty is usually quoted in, so it stays the default
 * and is sent as a number for the batch to keep. Years is the same thing times
 * twelve. Days and an explicit date cannot be expressed in whole months, so those
 * are worked out here against the invoice date and sent as the end date - which the
 * backend takes in preference to any duration.
 */
function warrantyFor(
  invoiceDate: string,
  unit: 'MONTHS' | 'YEARS' | 'DAYS' | 'DATE',
  value: string,
  date: string,
): { warrantyMonths: number | null; warrantyEndDate: string | null } {
  if (unit === 'DATE') {
    return { warrantyMonths: null, warrantyEndDate: date || null };
  }

  const amount = Number(value);
  if (!amount || amount <= 0) {
    return { warrantyMonths: null, warrantyEndDate: null };
  }
  if (unit === 'MONTHS') {
    return { warrantyMonths: amount, warrantyEndDate: null };
  }
  if (unit === 'YEARS') {
    return { warrantyMonths: amount * 12, warrantyEndDate: null };
  }

  const end = new Date(invoiceDate);
  end.setDate(end.getDate() + amount);
  return { warrantyMonths: null, warrantyEndDate: end.toISOString().slice(0, 10) };
}

/** What the row shows: the months given, or the date it runs to. */
function warrantyLabel(row: PurchaseRow): string {
  if (row.warrantyEndDate) return `till ${shortDate(row.warrantyEndDate)}`;
  if (row.warrantyMonths) return `${row.warrantyMonths} m`;
  return '-';
}

/** Per-handset costs add up; otherwise it is cost times quantity. */
function lineTotal(row: PurchaseRow): number {
  if (row.serialTracked && row.commonPrice === false) {
    return row.imeis.reduce((sum, entry) => sum + (Number(entry.purchasePrice) || 0), 0);
  }
  return (Number(row.purchasePrice) || 0) * (Number(row.quantity) || 0);
}

export default PurchaseAdd;
